import { ChangeOpacityMessage, ChangeOpacityMessages, SpinButtonState } from 'game-ui-types'

import { useBuyBonusStore, useConfigurationStore, useSpinButtonSectionStore } from '@stores'

export const BUY_BONUS_BUTTON_OPACITY_MESSAGE = ChangeOpacityMessages.CHANGE_OPACITY_BUY_BONUS_BUTTON

// Both idle states mean the round is over and the player is free to change the bet setup again.
const IDLE_STATES: SpinButtonState[] = [SpinButtonState.SEND_IDLE_STATE, SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE]

/**
 * The engine grays out the Buy Bonus button while a bought feature is running and only restores it
 * when the player collects the win. A feature that ends with 0 win has nothing to collect, so the
 * button is disabled again when the engine brings the rest of the UI back. With
 * `shouldTheFeatureStayActive` it also stays toggled, so every following spin buys the bonus again
 * and the player has no way to switch it off.
 *
 * Re-assert the default state on top of the engine's own opacity message, so the button reappears
 * together with the rest of the UI instead of flashing in ahead of it. The engine dims the button to
 * `inactiveOpacityValue` (0.5) next to dropping the interactivity, so both have to be overridden -
 * only its fade and duration are kept, to stay in sync with the rest of the UI coming back.
 */
export const restoreBuyBonusButtonInteractivity = (payload: ChangeOpacityMessage['payload']) => {
    // The engine left the button usable, or it is hidden - wait for the message that brings it back.
    if (payload.isInteractive || !payload.opacity) return

    const { hasBuyBonus, buyBonusConfig, isFeatureRunning } = useConfigurationStore.getState()

    if (!hasBuyBonus || !buyBonusConfig.shouldTheFeatureStayActive || isFeatureRunning) return

    if (!useBuyBonusStore.getState().isMultiChoiceToggled) return

    // Mid-round the button is disabled on purpose - the buy spin must not be interrupted.
    if (!IDLE_STATES.includes(useSpinButtonSectionStore.getState().state)) return

    const changeOpacityMessage: ChangeOpacityMessage = {
        type: BUY_BONUS_BUTTON_OPACITY_MESSAGE,
        payload: {
            ...payload,
            opacity: 1,
            isInteractive: true,
        },
    }

    window.uiBus.postMessage(changeOpacityMessage)
}
